import React, { useReducer } from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'
import htmlParse from 'html-react-parser'

import Accordion from 'components/Accordion'
import CollapsibleText from 'components/CollapsibleText'
import Icon from 'components/Icon'
import Tags from 'components/Review/components/Tags'
import Tooltip from 'components/Tooltip'
import Typography from 'components/Typography'
import { EventContext, useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import { NO_REVIEWS_COPY } from '../../constants'
import ListingIconName from '../ListingIconName'

import classes from './ReviewContent.module.scss'

const noop = () => {}

/** @type {import('./types').default} */
const ReviewContent = ({
  cons,
  content = '',
  created,
  hasSearch = false,
  listingDetailUrl,
  listingName,
  listingLogoUrl,
  onExpandClick = noop,
  onExpandPanelClick = noop,
  onListingIconNameClick = noop,
  pros,
  title,
  vendorReply = '',
  withListingIconName,
  component = 'h2',
  language,
  translationTitle,
  translationComments,
  translationPros,
  translationCons,
  profileReviewCard = false,
  review,
  showEnglishByDefault,
}) => {
  const hasTranslation = !!(translationTitle || translationComments || translationPros || translationCons)
  const hasEnglishFirst = hasTranslation && showEnglishByDefault
  const [hasToTranslate, toggle] = useReducer(s => !s, hasEnglishFirst)
  const { addEvent } = useEventContext()
  const { up } = useViewport()

  const handleExpandClick =
    name =>
    (event, { isExpanded }) =>
      onExpandClick(event, { isExpanded, name })
  const handleExpandPanelClick =
    name =>
    (event, { isExpanded }) =>
      onExpandPanelClick(event, { isExpanded, name })

  const reviewTitle = (hasToTranslate && translationTitle ? translationTitle : title) || NO_REVIEWS_COPY.title
  return (
    <Accordion
      className="ReviewContent"
      summaryClassname={classes.summaryPanel}
      summary={
        <>
          {profileReviewCard && up('lg') && (
            <ListingIconName
              className={classes.listingIconName}
              href={listingDetailUrl}
              name={listingName}
              logoUrl={listingLogoUrl}
              onClick={onListingIconNameClick}
            />
          )}
          {withListingIconName && !profileReviewCard && (
            <ListingIconName
              className={classes.listingIconName}
              href={listingDetailUrl}
              name={listingName}
              logoUrl={listingLogoUrl}
              onClick={onListingIconNameClick}
            />
          )}
          <Typography component={component} variant="heading-sm" className={clsx(classes.title, classes.highlightText)}>
            {hasSearch ? htmlParse(reviewTitle) : reviewTitle}
          </Typography>
          <div className={classes.tooltipContainer}>
            <Tooltip
              className={classes.tooltipText}
              enterTouchDelay={100}
              leaveTouchDelay={3000}
              title={dayjs(created).format('LL')}
            >
              <span className={classes.tooltipLabel}>
                <Typography component="span" variant="tight-xs" className={classes.formatDate}>
                  Reviewed {dayjs(created).fromNow()}
                </Typography>
              </span>
            </Tooltip>
            {review?.user_info && (
              <EventContext value={{ engagementComponent: 'product_reviews_card-item' }}>
                <Tags userInfo={review?.user_info} classname={classes.tagsExp} reviewDate={review.created} />
              </EventContext>
            )}
          </div>
        </>
      }
      detailsClassname={classes.detailsPanelExp}
      details={
        <>
          {content && (
            <CollapsibleText
              className={clsx(classes.row, classes.content)}
              hasSearch={hasSearch}
              maxNumberChars={400}
              offset={1}
              text={hasToTranslate && translationComments ? translationComments : content}
              textVariant="body-sm"
              onButtonClick={handleExpandClick('content')}
            />
          )}

          <div className={classes.row}>
            <Icon className={clsx(classes.iconSnippet, classes.iconPros)} icon="caret-circle-up" />
            <Typography className={classes.rowHeader} component="span" variant="heading-xs">
              Pros
            </Typography>

            {pros ? (
              <CollapsibleText
                className={clsx(classes.row, classes.snippet)}
                hasSearch={hasSearch}
                maxNumberChars={400}
                text={hasToTranslate && translationPros ? translationPros : pros}
                textVariant="body-xs"
                offset={1}
                onButtonClick={handleExpandClick('pros')}
              />
            ) : (
              <Typography className={clsx(classes.row, classes.snippet)} variant="body-xs">
                {NO_REVIEWS_COPY.pros}
              </Typography>
            )}
          </div>
          <div className={classes.row}>
            <Icon className={clsx(classes.iconSnippet, classes.iconCons)} icon="caret-circle-down" />
            <Typography className={classes.rowHeader} component="span" variant="heading-xs">
              Cons
            </Typography>

            {cons ? (
              <CollapsibleText
                className={clsx(classes.row, classes.snippet)}
                hasSearch={hasSearch}
                maxNumberChars={400}
                text={hasToTranslate && translationCons ? translationCons : cons}
                textVariant="body-xs"
                offset={1}
                onButtonClick={handleExpandClick('cons')}
              />
            ) : (
              <Typography className={clsx(classes.row, classes.snippet)} variant="body-xs">
                {NO_REVIEWS_COPY.cons}
              </Typography>
            )}
          </div>

          {hasTranslation && language && language !== 'en' && (
            <Typography className={classes.translation} variant="tight-xs">
              {hasToTranslate ? 'This review was translated from ' : 'Showing original review in '}
              {new Intl.DisplayNames(['en'], { type: 'language' }).of(language)}.{' '}
              <Typography
                variant="tight-xs"
                component="a"
                onClick={toggle}
                {...addEvent({
                  engagementComponent: 'translation',
                  engagementDestination: `read_${hasToTranslate ? 'original_review' : 'translation'}`,
                  engagementDetails: `text-link_${hasToTranslate ? 'original_review' : 'translation'}`,
                  name: 'engagement_product_reviews',
                })}
              >
                {hasToTranslate ? 'Read original review' : 'See translation'}
              </Typography>
            </Typography>
          )}

          {vendorReply && (
            <div className={classes.row}>
              <Typography className={classes.rowHeader} component="span" variant="heading-xs">
                Vendor response
              </Typography>
              <CollapsibleText
                className={classes.vendorReply}
                hasSearch={hasSearch}
                maxNumberChars={400}
                text={vendorReply}
                textVariant="body-xs"
                offset={1}
                onButtonClick={handleExpandClick('vendor-response')}
              />
            </div>
          )}
        </>
      }
      defaultExpanded
      disabled={up('sm')}
      onExpandClick={handleExpandPanelClick('pros-cons')}
    />
  )
}

export default ReviewContent
